import React from 'react';
import { getSourceBadge } from '../../utils/formatDetection';

/**
 * TrackSourceBadge — small "Local / Qobuz / NAS" pill for queue items.
 *
 * Usage:
 *   <TrackSourceBadge track={item.track} />
 *   <TrackSourceBadge track={item.track} unavailable />
 *
 * Pure UI — does not modify queue state. Per the spec, hooking up actual
 * skip-on-unavailable behavior is reserved for a separate transport-layer
 * prompt; this badge only surfaces the situation to the user.
 */

const COLORS = {
  Local:   { bg: 'rgba(255,255,255,0.06)', fg: 'var(--text-secondary)' },
  NAS:     { bg: 'rgba(91,164,229,0.10)',  fg: '#5BA4E5' },
  Qobuz:   { bg: 'rgba(0,123,255,0.10)',   fg: '#7AB7FF' },
  Tidal:   { bg: 'rgba(255,255,255,0.10)', fg: '#cfcfcf' },
  Spotify: { bg: 'rgba(30,215,96,0.12)',   fg: '#1ED760' },
  Stream:  { bg: 'rgba(201,168,76,0.12)',  fg: '#C9A84C' },
};

export default function TrackSourceBadge({ track, unavailable = false, className = '' }) {
  const label = getSourceBadge(track);
  if (!label && !unavailable) return null;
  const c = COLORS[label] || { bg: 'rgba(255,255,255,0.06)', fg: 'var(--text-tertiary)' };

  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      title={
        unavailable
          ? 'Source offline — track may not play'
          : `From ${label}`
      }
      style={{
        padding: '1px 6px',
        borderRadius: 4,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        background: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
        opacity: unavailable ? 0.7 : 1,
      }}
    >
      {unavailable && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      )}
      {label || 'Offline'}
    </span>
  );
}
